import { BottomSheet } from "@Light/components/page/BottomSheet";
import { Button } from "@Light/components/page/Button";
import { useOverlay } from "@Light/components/page/Overlay";
import { SuccessBanner } from "@Light/components/page/SuccessBanner";
import { Input, Select } from "@Light/components/system/Input";
import { useScaffold } from "@Light/scaffold";
import { OptOutReasonOption } from "@Light/services/lightTypes";
import { clsx } from "clsx";
import { useState, type ReactNode } from "react";
import { BylineText } from "./BylineText";
import { shuffleReasons } from "./shuffleReasons";

export type OptOutSelection = {
  reason: string;
  label?: string;
  detail?: string;
};

export type OptOutForcedChoice = {
  title: ReactNode;
  description?: ReactNode;
};

export type OptOutSurveyProps = {
  open: boolean;
  reasons: OptOutReasonOption[];
  isSubmitting?: boolean;
  forcedChoice?: OptOutForcedChoice;
  onClose: () => void;
  onExit: (selection: OptOutSelection | undefined) => void;
};

type OptOutSurveyActionsProps = {
  primaryLabel: string;
  isSubmitting?: boolean;
  onPrimaryClick: () => void;
  onClose: () => void;
};

function OptOutSurveyActions({
  primaryLabel,
  isSubmitting = false,
  onPrimaryClick,
  onClose,
}: OptOutSurveyActionsProps) {
  return (
    <div className="flex flex-col gap-2">
      <Button
        className="w-full"
        disabled={isSubmitting}
        onClick={onPrimaryClick}
      >
        {primaryLabel}
      </Button>
      <Button
        buttonType="outline"
        className="w-full"
        disabled={isSubmitting}
        onClick={onClose}
      >
        Return to plan
      </Button>
    </div>
  );
}

type OptOutSurveyShellProps = {
  body: ReactNode;
  footer: ReactNode;
};

function OptOutSurveyShell({ body, footer }: OptOutSurveyShellProps) {
  const scaffold = useScaffold();

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto px-4">
        <h2
          className={clsx(
            "pt-6 pb-6 text-2xl font-semibold leading-8",
            scaffold.page.colors.text.primary,
          )}
        >
          Before you exit, can you let us know why you didn&rsquo;t continue?
        </h2>
        {body}
      </div>

      <div className="shrink-0 px-4 pt-4 pb-6">{footer}</div>
    </div>
  );
}

type OptOutSurveyContentProps = {
  reasons: OptOutReasonOption[];
  isSubmitting?: boolean;
  onClose: () => void;
  onExit: (selection: OptOutSelection | undefined) => void;
};

function OptOutSurveyContent({
  reasons,
  isSubmitting = false,
  onClose,
  onExit,
}: OptOutSurveyContentProps) {
  const scaffold = useScaffold();
  const [shuffledReasons] = useState(() => shuffleReasons(reasons));
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [detailText, setDetailText] = useState("");

  const selectedOption = shuffledReasons.find(
    (reason) => reason.reason === selectedReason,
  );
  const primaryLabel = selectedReason ? "Submit and exit" : "Skip and exit";

  const handleExit = () => {
    if (!selectedReason || !selectedOption) {
      onExit(undefined);
      return;
    }

    const trimmedDetail = detailText.trim();

    onExit({
      reason: selectedReason,
      label: selectedOption.label,
      detail: trimmedDetail || undefined,
    });
  };

  return (
    <OptOutSurveyShell
      body={
        <div className="flex flex-col gap-3">
          {shuffledReasons.map((reason) => {
            const isSelected = selectedReason === reason.reason;
            const followUpPlaceholder = reason.follow_up_placeholder;

            return (
              <div
                key={reason.reason}
                className={clsx(
                  "flex flex-col",
                  followUpPlaceholder && isSelected && "gap-1.5",
                )}
              >
                <label
                  className={clsx(
                    "flex cursor-pointer items-start gap-3 rounded-xl border p-4",
                    isSelected
                      ? "border-2 border-brand bg-primary"
                      : "border-secondary bg-primary",
                  )}
                >
                  <input
                    type="radio"
                    name="opt-out-reason"
                    value={reason.reason}
                    checked={isSelected}
                    onChange={() => {
                      setSelectedReason(reason.reason);
                      setDetailText("");
                    }}
                    className="mt-0.5 size-4 shrink-0"
                  />
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <span
                      className={clsx(
                        "text-sm font-medium leading-5",
                        scaffold.page.colors.text.secondary,
                      )}
                    >
                      {reason.label}
                    </span>
                    {isSelected && reason.byline && (
                      <BylineText
                        text={reason.byline}
                        className={clsx(
                          "text-sm leading-5",
                          scaffold.page.colors.text.tertiary,
                        )}
                      />
                    )}
                  </span>
                </label>

                {followUpPlaceholder && isSelected && (
                  <Input
                    value={detailText}
                    onChange={(event) => {
                      setDetailText(event.target.value);
                    }}
                    placeholder={followUpPlaceholder}
                    aria-label={followUpPlaceholder}
                    autoFocus
                    className="focus:outline-none focus:!border-secondary focus:!ring-0"
                  />
                )}
              </div>
            );
          })}
        </div>
      }
      footer={
        <OptOutSurveyActions
          primaryLabel={primaryLabel}
          isSubmitting={isSubmitting}
          onPrimaryClick={handleExit}
          onClose={onClose}
        />
      }
    />
  );
}

type ForcedChoiceContentProps = OptOutSurveyContentProps & {
  forcedChoice: OptOutForcedChoice;
};

function ForcedChoiceContent({
  forcedChoice,
  reasons,
  isSubmitting = false,
  onClose,
  onExit,
}: ForcedChoiceContentProps) {
  const scaffold = useScaffold();
  const Field = scaffold.system.field;
  const Label = scaffold.system.label;
  const [shuffledReasons] = useState(() => shuffleReasons(reasons));
  const [selectedReason, setSelectedReason] = useState("");
  const [detailText, setDetailText] = useState("");
  const [showError, setShowError] = useState(false);

  const selectedOption = shuffledReasons.find(
    (reason) => reason.reason === selectedReason,
  );

  const handleExit = () => {
    if (!selectedOption) {
      setShowError(true);
      return;
    }
    onExit({
      reason: selectedOption.reason,
      label: selectedOption.label,
      detail: detailText.trim() || undefined,
    });
  };

  return (
    <div className="flex flex-col gap-3xl overflow-y-auto px-xl pt-3xl pb-xl expanded:p-3xl">
      <div className="flex flex-col gap-xs expanded:pr-6xl">
        <h2 className="text-display-xs font-semibold text-primary expanded:text-lg">
          {forcedChoice.title}
        </h2>
        {forcedChoice.description && (
          <p className="text-sm text-tertiary">{forcedChoice.description}</p>
        )}
      </div>
      <div className="flex flex-col gap-lg">
        <Select
          aria-label="Reason for opt out"
          data-testid="opt-out-reason-select"
          value={selectedReason}
          isError={showError && !selectedOption}
          onChange={(event) => {
            setSelectedReason(event.target.value);
            setDetailText("");
          }}
        >
          <option value="" disabled>
            Reason for opt out
          </option>
          {shuffledReasons.map((reason) => (
            <option key={reason.reason} value={reason.reason}>
              {reason.label}
            </option>
          ))}
        </Select>
        {selectedOption?.byline && (
          <SuccessBanner key={selectedOption.reason}>
            <BylineText text={selectedOption.byline} />
          </SuccessBanner>
        )}
        {selectedOption?.follow_up_placeholder && (
          <Field className="flex flex-col gap-sm">
            <Label className="text-sm">
              {selectedOption.follow_up_placeholder}
            </Label>
            <Input
              value={detailText}
              onChange={(event) => {
                setDetailText(event.target.value);
              }}
            />
          </Field>
        )}
      </div>
      <div className="grid gap-md pt-xs expanded:grid-cols-2 expanded:gap-lg">
        <Button
          buttonType="outline"
          className="expanded:order-last"
          disabled={isSubmitting}
          onClick={handleExit}
          data-testid="opt-out-confirm-button"
        >
          Yes, I&rsquo;m sure
        </Button>
        <Button disabled={isSubmitting} onClick={onClose}>
          Go back
        </Button>
      </div>
    </div>
  );
}

export function OptOutSurvey({
  open,
  reasons,
  isSubmitting = false,
  forcedChoice,
  onClose,
  onExit,
}: OptOutSurveyProps) {
  const { isExpanded } = useOverlay();
  const reasonsKey = reasons.map((reason) => reason.reason).join(",");

  if (forcedChoice) {
    return (
      <BottomSheet
        open={open}
        onClose={onClose}
        hideCloseButton={!isExpanded}
        scrollable={false}
        modalWhenExpanded
      >
        <ForcedChoiceContent
          key={reasonsKey}
          forcedChoice={forcedChoice}
          reasons={reasons}
          isSubmitting={isSubmitting}
          onClose={onClose}
          onExit={onExit}
        />
      </BottomSheet>
    );
  }

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      hideCloseButton
      scrollable={false}
    >
      <OptOutSurveyContent
        key={reasonsKey}
        reasons={reasons}
        isSubmitting={isSubmitting}
        onClose={onClose}
        onExit={onExit}
      />
    </BottomSheet>
  );
}
